import { useMediaQuery } from '@mui/material'
import cx from 'classnames'
import isEmpty from 'lodash.isempty'
import PubSub from 'pubsub-js'
import React, { useEffect, useMemo, useState } from 'react'

import { get } from '@js/common/api'
import { getUrl } from '@js/common/getUrlByDomain'
import User from '@js/common/user'
import {
  formatNumber,
  getUUIDv4,
  isVendorRelatedType,
  staticAsset
} from '@js/common/utils'

import CTA from '@f/elements/CTA'
import Icon from '@f/elements/Icon'

import DetachedFloorPlanFilter from '@f-fertighaus/applications/SearchResults/partials/DetachedFloorPlanFilter'
import { triggerSearchResultsTracking } from '@f-fertighaus/utils/trackingUtils'

import SearchResultsHouse from './partials/items/SearchResultsHouse'
import SearchResultsVendor from './partials/items/SearchResultsVendor'
import SearchResultsFilters from './partials/SearchResultsFilters'
import SearchResultsFiltersBar from './partials/SearchResultsFiltersBar'
import SearchResultsPagination from './partials/SearchResultsPagination'
import SearchResultsSkeleton from './SearchResultsSkeleton'

const SearchResults = ({
  type,
  location,
  availableFilters = [],
  availableSorting = [],
  isSearchResultsPage = false,
  openSetLocationModal = () => {},
  initialParams = {},
  ...props
}) => {
  const [apiResult, setApiResult] = useState({})
  const [loaded, setLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [panelDisplayed, setPanelDisplayed] = useState(false)
  const [banner, setBanner] = useState(null)
  const isMobileView = useMediaQuery('(max-width: 767px)')
  const isDesktopView = useMediaQuery('(min-width: 901px)')

  const isExLeadsVariant = props.variant === 'ex_leads'

  const paramsToQueryString = paramsToParse => {
    if (!paramsToParse || Object.keys(paramsToParse) < 1) {
      return ''
    }

    let string = ''

    Object.keys(paramsToParse).map((param, index) => {
      if (paramsToParse[param]) {
        string += (index > 0 ? '&' : '') + param + '=' + paramsToParse[param]
      }
    })

    return `?${string}`
  }

  const updateHreflangTags = query => {
    const hrefLangTags = [...document.querySelectorAll('[hreflang]')]

    hrefLangTags.map(tag => {
      tag.href = tag.href.split('?')[0] + (query === '?' ? '' : query)
    })
  }

  let postcodeLocalStorage = User.getLocation() && User.getLocation().postcode

  if (
    postcodeLocalStorage &&
    initialParams['delivery_regions__in'] !== postcodeLocalStorage
  ) {
    initialParams['delivery_regions__in'] = postcodeLocalStorage
  }

  if (/price/.test(initialParams['order_by'])) {
    initialParams['order_by'] = initialParams['order_by'].split('_')[0]
  }

  Object.keys(initialParams).map(param => {
    if (/^price/.test(param) && !/range$/.test(param)) {
      let paramValue = initialParams[param]

      delete initialParams[param]

      initialParams[`${param}__range`] = paramValue
    }
  })

  const [params, setParams] = useState(initialParams)

  const filterParams = ({ params = {}, type = '' }) => {
    let allowedFilters = [
      'price',
      'price_ready',
      'price_fitting',
      'price_lego',
      'expansion_stage',
      'living_area_total',
      'construction_method',
      'number_rooms',
      'delivery_regions',
      'form',
      'roof_shape',
      'styles',
      'utilization',
      'energy_standards',
      'number_storeys',
      'funding',
      'vendor',
      'vendor_nid',
      'target_groups',
      'site',
      'has_floor_plans'
    ]

    if (props.forcedParams && props.forcedParams.length > 0) {
      allowedFilters = allowedFilters.filter(
        filter => !props.forcedParams.find(param => param.name === filter)
      )
    }

    let returnParams = {}

    for (let key in params) {
      if (
        type === 'filters' &&
        allowedFilters.indexOf(key.split('__')[0]) > -1
      ) {
        if (isVendorRelatedType(key)) {
          const availableVendors = availableFilters.find(
            filter => filter.name === 'vendor_nid'
          ).values

          let vendors = params[key].split(',')

          for (const currentVendor of vendors) {
            if (
              !availableVendors.find(
                availableVendor => availableVendor.nid === currentVendor
              )
            ) {
              vendors = vendors.filter(vendor => vendor !== currentVendor)
            }
          }

          returnParams[key] = vendors.join(',')
        } else {
          returnParams[key] = params[key]
        }
      } else if (type === 'sorting' && key === 'order_by') {
        returnParams[key] = params[key]
      }
    }

    return returnParams
  }

  const getDeprecatedParams = params => {
    let returnObj = {}

    if (!params) {
      return {}
    }

    Object.keys(params).map(param => {
      if (/__range/.test(param)) {
        let value = params[param].split(',')
        let valueObject = {}

        if (value[0] !== '') {
          valueObject['startsWith'] = value[0]
        }

        if (value[1] !== '') {
          valueObject['endsWith'] = value[1]
        }

        returnObj[param.split('__')[0]] = { ...valueObject }
      }

      if (/has_floor_plans/.test(param)) {
        returnObj.has_floor_plans = {}
      }

      if (/__in/.test(param)) {
        returnObj[param.split('__')[0]] = { within: params[param].split(',') }
      }
    })

    return returnObj
  }

  const device =
    (isMobileView && 'mobile') || (isDesktopView && 'desktop') || 'tablet'

  const isStringOrUndefinedLimit =
    !props.hardLimit || typeof props.hardLimit === 'string'

  const hardLimitMax = isStringOrUndefinedLimit
    ? props.hardLimit
    : props.hardLimit[device]

  const defaultHouseLimit = isExLeadsVariant ? 12 : 24

  const defaultLimitByType = type === 'houses' ? defaultHouseLimit : 200

  /* Limit for vendors was put to 1000 but actual limit on BE side is 200 */
  const limit = hardLimitMax ? hardLimitMax : defaultLimitByType

  const isEmbedded =
    props.variant === 'hlp_vendor_list_embedded' ||
    props.variant === 'ua_embedded' ||
    props.variant === 'hlp_embedded'

  const updateSearchResults = () => {
    setIsLoading(true)

    let searchParams = { ...params }

    if (searchParams.hasOwnProperty('vendor_nid__in')) {
      const availableVendors = availableFilters.find(
        filter => filter.name === 'vendor_nid'
      ).values
      let vendors = searchParams['vendor_nid__in'].split(',')

      for (const currentVendor of vendors) {
        if (
          !availableVendors.find(
            availableVendor => availableVendor.nid === currentVendor
          )
        ) {
          vendors = vendors.filter(vendor => vendor !== currentVendor)
        }
      }

      searchParams['vendor_nid__in'] = vendors.join(',')
    }

    if (
      searchParams.hasOwnProperty('order_by') &&
      /price/.test(searchParams['order_by'])
    ) {
      if (searchParams.hasOwnProperty('price_ready__range')) {
        searchParams['order_by'] = searchParams['order_by'] + '_ready'
      } else if (searchParams.hasOwnProperty('price_fitting__range')) {
        searchParams['order_by'] = searchParams['order_by'] + '_fitting'
      } else if (searchParams.hasOwnProperty('price_lego__range')) {
        searchParams['order_by'] = searchParams['order_by'] + '_lego'
      }
    }

    searchParams.limit = limit

    if (props.forcedParams && props.forcedParams.length > 0) {
      for (const param of props.forcedParams) {
        searchParams[`${param.name}__in`] = param.value
      }
    }

    let paramsWithoutPrice = { ...searchParams }
    Object.keys(paramsWithoutPrice).map(param => {
      if (param.indexOf('price') === 0) {
        paramsWithoutPrice[param] = ','
      }
    })

    const seed = getSeed()
    get(
      type,
      props.vendorNid
        ? { ...searchParams, seed: seed, vendor_nid__in: props.vendorNid }
        : { ...searchParams, seed: seed }
    ).then(apiResponse => {
      delete searchParams.limit

      let searchQueryParams = getDeprecatedParams(searchParams)

      const displayResults = results => {
        if (/fu/.test(window['🍪'].read('cookie_policy_config'))) {
          const search = JSON.parse(localStorage.getItem('search') || '{}')
          const leads = JSON.parse(localStorage.getItem('leads') || '{}')

          localStorage.setItem(
            'search',
            JSON.stringify({ ...search, query: searchQueryParams })
          )
          localStorage.setItem(
            'leads',
            JSON.stringify({ ...leads, additional_payload: searchParams })
          )
        }

        if (type === 'houses') {
          let count = 1
          let highlights = {}
          for (let i = 0; i < results.length; i++) {
            if (results[i].isTopPlacement || results[i].isLocal) {
              let houseNid = results[i].nid
              highlights[houseNid] = {
                item_rank: count,
                house_nid: houseNid,
                type: results[i].isLocal
                  ? 'house_regional'
                  : 'house_top_placement',
                display_price: results[i].price_display,
                display_expansion_stage: results[i].price_label
              }
              count++
            }
          }

          let results_set = {}
          for (let i = 0; i < results.length; i++) {
            if (!results[i].isTopPlacement || !results[i].isLocal) {
              let houseNid = results[i].nid
              results_set[houseNid] = {
                item_rank: count,
                house_id: houseNid,
                display_price: results[i].price_display,
                display_expansion_stage: results[i].price_label
              }

              count++
            }
          }

          if (results.length > 0) {
            _wt({
              category: 'item-collection',
              action: 'display',
              label: 'search-results',
              values: results.map(house => ({
                type: 'house_nid',
                value: house.nid
              }))
            })
          }
        }

        const topPlacementsOrLocal = results.filter(
          el => el.isTopPlacement || el.isLocal
        )

        triggerSearchResultsTracking(
          searchParams,
          type,
          apiResponse.total_count,
          location
        )

        setApiResult(currentApiResult => ({
          ...currentApiResult,
          ...apiResponse,
          results: hardLimitMax
            ? results.slice(0, topPlacementsOrLocal.length + hardLimitMax)
            : results
        }))

        setIsLoading(false)
        setLoaded(true)
      }

      if (type === 'houses' && !props.disableTopPlacementAndBranding) {
        let results = apiResponse.results
        let topPlacementParams = {}

        let brandedHouses = []

        results = results
          .map(listing => {
            if (
              listing.vendor_verbose.branding_eye_catcher &&
              brandedHouses.indexOf(listing.vendor_verbose.nid) < 0
            ) {
              listing.isBranded = true
              brandedHouses.push(listing.vendor_verbose.nid)
            }
            return listing
          })
          .filter(house => !house.isLocal)

        const brandedLocalHouses = apiResponse.payload.top_local_placement || []

        if (brandedLocalHouses) {
          const brandedLocalHousesUniqeVendors = [
            ...new Set(brandedLocalHouses.map(item => item.vendor_verbose.nid))
          ]

          const brandedLocalHousesToShow = params.delivery_regions__in
            ? brandedLocalHousesUniqeVendors
                .map(nid =>
                  brandedLocalHouses.find(
                    house => house.vendor_verbose.nid === nid
                  )
                )
                .sort(() => 0.5 - Math.random())
                .slice(0, 3)
                .map(house => ({ isLocal: true, ...house }))
            : []

          results = [...brandedLocalHousesToShow, ...results]
        }

        Object.keys(searchParams).map(key => {
          let value = decodeURIComponent(searchParams[key])
          if (/__range/.test(key) && value.indexOf(',') < 0) {
            value = `${value},`
          }

          topPlacementParams[key] = value
        })

        get('data-services/house-top-placement/', topPlacementParams).then(
          topPlacement => {
            if (!topPlacement[0]) {
              displayResults(results)
              return
            }

            const request = {}
            Object.keys(searchParams).map(key => {
              let value = decodeURIComponent(searchParams[key])
              if (/__range/.test(key) && value.indexOf(',') < 0) {
                value = `${value},`
              }
              request[key] = value
            })
            if (topPlacement[0].house_nids) {
              request['nid__in'] = topPlacement[0].house_nids
                .map(house_nid => house_nid)
                .join(',')
            }

            if (User.getLocation().postcode) {
              request['delivery_regions__in'] = User.getLocation().postcode
            } else {
              const hostnameSplit = window.location.hostname.split('.')
              request['country'] = hostnameSplit.pop().toUpperCase()
            }

            if (request.nid__in) {
              get('houses', request).then(topPlacementHouses => {
                if (topPlacementHouses.results.length > 0) {
                  let housesToAttach = topPlacementHouses.results.map(house => {
                    house.isTopPlacement = true
                    return house
                  })

                  results = [...housesToAttach, ...results]
                }

                displayResults(results)
              })
            } else {
              displayResults(results)
            }
          },
          () => {
            displayResults(results)
          }
        )
      } else {
        if (/fu/.test(window['🍪'].read('cookie_policy_config'))) {
          const search = JSON.parse(localStorage.getItem('search') || '{}')
          localStorage.setItem(
            'search',
            JSON.stringify({ ...search, query: searchQueryParams })
          )
        }

        displayResults(apiResponse.results)
      }
    })

    /* Generate slots afterwards */
    if (type !== 'vendors') {
      get(
        type,
        props.vendorNid
          ? { ...paramsWithoutPrice, slots: 1, vendor_nid__in: props.vendorNid }
          : { ...paramsWithoutPrice, slots: 1 }
      ).then(apiResponse => {
        setApiResult(currentApiResult => ({
          ...currentApiResult,
          slots: apiResponse.payload && apiResponse.payload.slots
        }))
      })
    }
  }

  const updateParams = (paramsToSet = {}, options = {}) => {
    if (!options.isPagination) {
      delete paramsToSet['page']
    }

    setParams(paramsToSet)
  }

  const resetAllFilters = event => {
    event && event.preventDefault()
    const newSearchParams = {}
    if (params.has_floor_plans) {
      newSearchParams.has_floor_plans = 'true'
    }
    updateParams(newSearchParams)
  }

  const getSeed = () => {
    // Returns DD/MM/YYYY
    let today = () => {
      let now = new Date()
      return `${now.getUTCDate()}/${now.getUTCMonth()}/${now.getUTCFullYear()}`
    }

    let seed
    if (/fu/.test(window['🍪'].read('cookie_policy_config'))) {
      seed = JSON.parse(localStorage.getItem('api-list') || '{}').seed
    }

    if (!seed || seed.time !== today()) {
      seed = { time: today(), uuid: getUUIDv4() }

      if (/fu/.test(window['🍪'].read('cookie_policy_config'))) {
        localStorage.setItem('api-list', JSON.stringify({ seed }))
      }
    }

    return seed.uuid
  }

  const getBanner = () => {
    if (props.disableBanners === true) {
      return
    }

    // Build Local Hero API Params
    let bannerParams = {
      container: 'local_hero'
    }

    if (params.delivery_regions__in) {
      bannerParams['postal_codes'] = params.delivery_regions__in.slice(0, 8)
    }
    if (params.form__in) {
      bannerParams['house_form__in'] = params.form__in
    }

    // if filtering by specific vendor, or there is no delivery region
    // don't display the local hero banners
    if (!params.vendor_nid__in && params.delivery_regions__in) {
      // Get random Local Hero banner. If none, use SherlockContainer banner
      get('banners', bannerParams).then(response => {
        let localHeros = response.results.filter(banner => {
          return banner.container === 'local_hero'
        })
        if (localHeros.length > 0) {
          setBanner(localHeros[Math.floor(Math.random() * localHeros.length)])
        } else {
          setBanner(null)
        }
      })
    } else {
      setBanner(null)
    }
  }

  const displayPanel = ({ filter, offsetLeft, isActive }) => {
    if (!filter || filter === panelDisplayed.filter) {
      setPanelDisplayed(false)
    } else {
      setPanelDisplayed({
        filter,
        offsetLeft,
        isActive
      })
    }
  }

  const removeFilter = filter => {
    let paramsToSet = { ...params }
    const matchedParam = Object.keys(paramsToSet).find(param =>
      type === 'vendors' && filter === 'expansion_stage'
        ? param.indexOf('price') > -1
        : param.indexOf(filter) > -1
    )

    if (filter === 'expansion_stage' && type === 'vendors') {
      delete paramsToSet[matchedParam]
    } else if (
      filter === 'expansion_stage' &&
      paramsToSet[matchedParam] &&
      paramsToSet[matchedParam] !== ','
    ) {
      let value = paramsToSet[matchedParam]
      delete paramsToSet[matchedParam]
      paramsToSet['price'] = value
    } else {
      delete paramsToSet[matchedParam]
    }

    if (matchedParam === 'delivery_regions__in') {
      User.setLocation()
      PubSub.publish('QUERYUPDATE', { field: 'delivery_regions__in' })
    }

    updateParams(paramsToSet)
    displayPanel({})
  }

  const handleSorting = event => {
    const target = event.target

    if (target.name === 'vendor_direct') {
      window.location.pathname = target.value
    } else {
      let paramsToSet = { ...params }

      if (target.value === '') {
        delete paramsToSet['order_by']
      } else {
        paramsToSet['order_by'] = target.value
      }

      updateParams(paramsToSet)
    }

    target.blur()
  }

  let pbQueryupdate
  useEffect(() => {
    getBanner()

    pbQueryupdate = PubSub.subscribe('QUERYUPDATE', (msg, data) => {
      let paramsToSet = { ...params }

      if (data.postcode) {
        paramsToSet['delivery_regions__in'] = data.postcode
      } else if (paramsToSet.hasOwnProperty('delivery_regions__in')) {
        delete paramsToSet['delivery_regions__in']
      }

      updateParams(paramsToSet)
    })

    return () => {
      PubSub.unsubscribe(pbQueryupdate)
    }
  }, [params])

  useEffect(() => {
    if (isSearchResultsPage) {
      window.history.replaceState(
        null,
        document.title,
        Object.keys(params).length > 0
          ? paramsToQueryString(params)
          : window.location.pathname
      )
      updateHreflangTags(paramsToQueryString(params))
    }

    if (params) {
      updateSearchResults()
      getBanner()
    }
  }, [params])

  let resultsCount = 'Keine Ergebnisse gefunden'

  if (isLoading === true && type === 'houses') {
    resultsCount = 'Passende Häuser finden...'
  } else if (apiResult.total_count > 0 && isLoading === false) {
    switch (type) {
      case 'houses':
        if (apiResult.total_count == 1) {
          resultsCount = `${apiResult.total_count} Haus`
        } else {
          resultsCount = `${formatNumber(
            apiResult.total_count,
            0,
            ',',
            '.'
          )} Häuser`
        }
        break

      case 'vendors':
        resultsCount = `${formatNumber(
          apiResult.total_count,
          0,
          ',',
          '.'
        )} Anbieter`
        break
    }
  }

  let appliedFiltersRest = 0
  Object.keys(filterParams({ params, type: 'filters' })).map(el => {
    if (
      !/price__|living_area_total|delivery_regions|has_floor_plans|form/.test(
        el
      )
    ) {
      appliedFiltersRest++
    }
  })

  const topPlacementVendors = useMemo(() => {
    if (
      apiResult.payload &&
      apiResult.payload.top_placement &&
      apiResult.payload.top_placement.length > 0
    ) {
      return apiResult.payload.top_placement
        .slice()
        .sort(() => 0.5 - Math.random())
        .slice(0, 3)
        .reduce((acc, house) => {
          return house.branding_plan === 'premium'
            ? [house, ...acc]
            : [...acc, house]
        }, [])
        .map(vendor => ({
          ...vendor,
          isTopPlacement: true
        }))
    }
    return []
  }, [apiResult.payload])

  const topPlacementLocalVendors = useMemo(() => {
    if (
      apiResult.payload &&
      apiResult.payload.local_top_placement &&
      apiResult.payload.local_top_placement.length > 0 &&
      params.delivery_regions__in
    ) {
      return apiResult.payload.local_top_placement
        .sort(() => 0.5 - Math.random())
        .slice(0, 3)
        .map(vendor => ({
          ...vendor,
          isTopLocalPlacement: true
        }))
    }
    return []
  }, [apiResult.payload, params.delivery_regions__in])

  const topVendors = [...topPlacementVendors, ...topPlacementLocalVendors]
  const remainingVendors =
    apiResult.results &&
    apiResult.results.filter(
      vendor => !topVendors.find(topVendor => topVendor.nid === vendor.nid)
    )

  useEffect(() => {
    if (type === 'vendors' && Object.keys(apiResult).length) {
      let count = 1
      let results_set = {}
      let highlights = {}

      for (let i = 0; i < topPlacementVendors.length; i++) {
        let vendorNid = topPlacementVendors[i].nid
        highlights[vendorNid] = {
          item_rank: count,
          vendor_nid: vendorNid,
          type: 'vendor_top_placement'
        }
        count++
      }

      for (let i = 0; i < topPlacementLocalVendors.length; i++) {
        if (params.delivery_regions__in) {
          let vendorNid = topPlacementLocalVendors[i].nid
          highlights[vendorNid] = {
            item_rank: count,
            vendor_nid: vendorNid,
            type: 'vendor_regional'
          }
          count++
        }
      }

      for (let i = 0; i < remainingVendors.length; i++) {
        if (!remainingVendors[i].isTopPlacement) {
          let vendorNid = remainingVendors[i].nid
          results_set[vendorNid] = {
            item_rank: count,
            vendor_nid: vendorNid
          }
          count++
        }
      }

      if (topPlacementVendors.length > 0)
        _wt({
          category: 'item-collection',
          action: 'display',
          label: 'vendor-results-top',
          values: topPlacementVendors.map(vendor => ({
            type: 'vendor_nid',
            value: vendor.nid
          }))
        })

      if (topPlacementLocalVendors.length > 0)
        _wt({
          category: 'item-collection',
          action: 'display',
          label: 'vendor-results-regio',
          values: topPlacementLocalVendors.map(vendor => ({
            type: 'vendor_nid',
            value: vendor.nid
          }))
        })

      if (remainingVendors.length > 0)
        _wt({
          category: 'item-collection',
          action: 'display',
          label: 'vendor-results-standard',
          values: remainingVendors.map(vendor => ({
            type: 'vendor_nid',
            value: vendor.nid
          }))
        })
    }
  }, [apiResult])

  const isLocationSelected =
    !isEmpty(User.getLocation()) ||
    window.location.search.includes('delivery_regions__in')

  const vendorSelectValues =
    availableFilters.find(el => el.name === 'vendor') &&
    availableFilters.find(el => el.name === 'vendor').values

  let topPlacementsOrLocals =
    !isLoading && apiResult.total_count > 0
      ? apiResult.results.filter(el => el.isTopPlacement || el.isLocal).length
      : 0

  let results = isLoading ? (
    <SearchResultsSkeleton itemNumber={limit} />
  ) : apiResult.total_count > 0 ? (
    <>
      {type === 'houses' ? (
        <div className='[ f-d-flr f-fl-wrap f-fl-jc-ce ] SearchResults-items'>
          {apiResult.results
            .reduce((acc, house) => {
              return house.isTopPlacement &&
                house.vendor_verbose.branding_plan === 'premium'
                ? [house, ...acc]
                : [...acc, house]
            }, [])
            .map((house, index) => {
              const RenderedHouse = (
                <SearchResultsHouse
                  key={`search_result_${house.nid}_${index}`}
                  item={house}
                  onClick={props.onClick}
                  position={index + 1}
                  openSetLocationModal={openSetLocationModal}
                  disableTopPlacementAndBranding={
                    props.disableTopPlacementAndBranding
                  }
                  hardLimitOffset={topPlacementsOrLocals}
                  hardLimit={props.hardLimit}
                  variant={props.variant}
                  forcedVendorNid={
                    props.forcedVendorNid ? props.forcedVendorNid : null
                  }
                  showFloorPlansOnly={params.has_floor_plans}
                />
              )

              const RenderedBanner = banner ? (
                <div
                  className='[ f-p-a-030 f-t-centre ]'
                  style={{ width: '100%' }}>
                  <a href={banner.url} target='_blank' rel='noreferrer'>
                    <img
                      className='[ f-d-inbl f-hidden:s ]'
                      src={banner.file_desktop.url}
                      width={banner.file_desktop.width}
                      height={banner.file_desktop.height}
                    />
                    <img
                      className='[ f-d-inbl f-hidden:m f-hidden:l ]'
                      src={banner.file_mobile.url}
                      width={banner.file_mobile.width}
                      height={banner.file_mobile.height}
                    />
                  </a>
                </div>
              ) : null

              if (apiResult.results.length > 13) {
                return index === 12 && banner ? (
                  <React.Fragment key={`search_result_banner`}>
                    {RenderedBanner}
                    {RenderedHouse}
                  </React.Fragment>
                ) : (
                  RenderedHouse
                )
              }
              return apiResult.results.length === index + 1 ? (
                <React.Fragment key={`search_result_banner`}>
                  {RenderedHouse}
                  {RenderedBanner}
                </React.Fragment>
              ) : (
                RenderedHouse
              )
            })}
        </div>
      ) : (
        <>
          {topPlacementVendors.length > 0 && (
            <div className='[ f-c-bo-neu050 f-p-b-020 f-m-b-020 ] SearchResults-items-group'>
              <span className='[ f-d-bl f-t-tmedium f-c-fo-pri f-m-b-010 ]'>
                {isLocationSelected
                  ? 'Empfohlene Top Anbieter in Ihrer Region'
                  : 'Empfohlene Top Anbieter'}
              </span>
              <div
                className={`[ f-d-flr f-fl-wrap ${
                  topPlacementVendors.length < 3 ? 'f-fl-jc-st' : 'f-fl-jc-ce'
                } ] SearchResults-items`}>
                {topPlacementVendors.map((vendor, index) => (
                  <SearchResultsVendor
                    key={`search_result_top_placement_${vendor.nid}_${index}`}
                    item={vendor}
                    position={index + 1}
                    openSetLocationModal={openSetLocationModal}
                  />
                ))}
              </div>
            </div>
          )}
          {topPlacementLocalVendors.length > 0 && params.delivery_regions__in && (
            <div className='[ f-c-bo-neu050 f-p-b-020 f-m-b-020 ] SearchResults-items-group'>
              <span className='[ f-d-bl f-t-tmedium f-c-fo-pri f-m-b-010 ]'>
                Empfohlene lokale Anbieter in Ihrer Region
              </span>
              <div
                className={`[ f-d-flr f-fl-wrap ${
                  topPlacementLocalVendors.length < 3
                    ? 'f-fl-jc-st'
                    : 'f-fl-jc-ce'
                } ] SearchResults-items `}>
                {topPlacementLocalVendors.map((vendor, index) => (
                  <SearchResultsVendor
                    key={`search_result_top_placement_${vendor.nid}_${index}`}
                    item={vendor}
                    position={topPlacementVendors.length + index + 1}
                    openSetLocationModal={openSetLocationModal}
                  />
                ))}
              </div>
            </div>
          )}
          {remainingVendors.length > 0 && (
            <div className='[ f-p-b-020 f-m-b-020 ]'>
              {topVendors.length > 0 && (
                <span className='[ f-d-bl f-t-tmedium f-c-fo-pri f-m-b-010 ]'>
                  {`${apiResult.total_count - topVendors.length} weitere${
                    apiResult.total_count - topVendors.length === 1 ? 'r' : ''
                  } ${
                    isLocationSelected ? 'Anbieter in Ihrer Region' : 'Anbieter'
                  }`}
                </span>
              )}
              <div
                className={`[ f-d-flr f-fl-wrap ${
                  topPlacementLocalVendors.length < 3
                    ? 'f-fl-jc-st'
                    : 'f-fl-jc-ce'
                } ] SearchResults-items`}>
                {remainingVendors.map((vendor, index) => (
                  <SearchResultsVendor
                    key={`search_result_${vendor.nid}_${index}`}
                    item={vendor}
                    position={topVendors.length + index + 1}
                    openSetLocationModal={openSetLocationModal}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {apiResult.total_count > apiResult.results.length && !isEmbedded && (
        <SearchResultsPagination
          page={params.page || 1}
          totalCount={apiResult.total_count}
          resultCount={24}
          params={params}
          updateParams={updateParams}
          variant={props.variant}
        />
      )}
    </>
  ) : null

  const floorPlansFilter =
    !isMobileView &&
    availableFilters.find(filter => filter.name === 'has_floor_plans')

  const availableDefaultFilters = isMobileView
    ? availableFilters
    : availableFilters.filter(filter => filter.name !== 'has_floor_plans')

  return (
    <>
      <div
        className={cx(
          'SearchResultsFilters',
          !isEmbedded && props.variant !== `ex_leads` && 'is-section is-light'
        )}>
        <div className='[ f-wrap ]' data-testid='filter-panel'>
          <span className='[ f-d-bl f-p-t-020 f-t-tbody f-c-fo-pri ]'>
            Filter
          </span>
          <div className='f-d-iflr f-fl-jc-sb' style={{ width: '100%' }}>
            <div className='[ f-d-flr ] SearchResultsFilters-wrapper'>
              <SearchResultsFiltersBar
                params={params}
                filters={filterParams({
                  params: params,
                  type: 'filters'
                })}
                type={type}
                apiResult={apiResult}
                updateParams={updateParams}
                availableFilters={availableDefaultFilters}
                resetAllFilters={resetAllFilters}
                displayPanel={displayPanel}
                panelDisplayed={panelDisplayed}
                variant={props.variant}
              />
              {type === 'vendors' && (
                <CTA
                  type='block-tertiary'
                  tag='a'
                  href={getUrl('vendorMap')()}
                  className='[ f-p-a-010 f-m-a-005 f-hidden:s ] SearchResultsFilters-button VendorMap-link'>
                  <Icon
                    icon='map-16px'
                    className='[ f-icon-020 f-c-fi-inherit f-icon-t-mini f-m-r-005 ]'
                  />
                  Karte anzeigen
                </CTA>
              )}
              {type === 'houses' && !isExLeadsVariant && (
                <CTA
                  type='block-tertiary'
                  tag='button'
                  className={`[ f-p-a-010 f-m-a-005 ] SearchResultsFilters-button ${
                    appliedFiltersRest > 0 ? 'is-active' : ''
                  }`}
                  data-sel='btn-flt-addi'
                  onClick={event =>
                    displayPanel({
                      filter: 'more-filters',
                      offsetLeft: event.currentTarget.offsetLeft,
                      isActive: appliedFiltersRest > 0
                    })
                  }
                  wtData={{
                    'CTA-click': {
                      label: 'filter-type',
                      location: 'filter-panel'
                    }
                  }}>
                  <span className='[ f-d-inbl f-p-h-005 ]'>
                    Weitere Filter
                    {appliedFiltersRest > 0 ? ` · ${appliedFiltersRest}` : ''}
                  </span>
                </CTA>
              )}
            </div>
            {floorPlansFilter && (
              <DetachedFloorPlanFilter
                filter={floorPlansFilter}
                updateParams={updateParams}
                activeFilterParams={params}
              />
            )}
          </div>
        </div>

        <SearchResultsFilters
          panelDisplayed={panelDisplayed}
          removeFilter={removeFilter}
          params={params}
          filters={filterParams({
            params: params,
            type: 'filters'
          })}
          type={type}
          updateParams={updateParams}
          availableFilters={availableFilters}
          resetAllFilters={resetAllFilters}
          displayPanel={displayPanel}
          variant={props.variant}
          apiResult={apiResult}
        />
      </div>

      {loaded ? (
        <div
          className={cx(
            'SearchResults__wrapper',
            isExLeadsVariant ? 'SearchResults__wrapper--ex-leads' : 'is-light'
          )}>
          <div
            className={`[ f-wrap ] ${
              isLoading ? 'SearchResults-initial-min-height' : ''
            }`}>
            {props.variant !== 'hlp_vendor_list_embedded' && (
              <div
                className={`[ ${
                  apiResult.total_count > 0
                    ? 'f-d-flr f-fl-jc-sb f-fl-al-ce'
                    : ''
                } f-p-v-020 ] SearchResults-header`}>
                <span
                  className='[ f-d-bl f-t-body f-t-bold f-c-fo-pri ]'
                  data-sel='txt-rslt'>
                  {resultsCount}
                </span>

                {apiResult.total_count < 1 && !isLoading && (
                  <>
                    <CTA
                      type='text-primary'
                      tag='button'
                      onClick={resetAllFilters}
                      className='[ f-t-body ]'>
                      Filter zurücksetzen
                    </CTA>
                    <div className='[ f-t-centre f-p-h-040 f-p-h-080:m f-p-b-080 f-p-t-040 ]'>
                      <img
                        style={{ opacity: 0.6 }}
                        className='[ f-d-inbl ]'
                        width='400px'
                        src={staticAsset(
                          'images/at_f/construction_method_2.svg'
                        )}
                      />
                    </div>
                  </>
                )}

                {type === 'vendors' && apiResult.total_count > 0 && (
                  <div className='[ f-pos-rel ] SearchResults-sorting'>
                    <span className='[ f-t-micro f-c-fo-sh080 ]'>
                      Direkt zum Anbieter
                      <Icon
                        icon='search-16px'
                        className='[ f-icon-020 f-c-st-bla f-m-l-010 f-icon-t-mini ] SearchResults-sorting-icon'
                      />
                    </span>
                    <select
                      defaultValue='default'
                      name='vendor_direct'
                      className='[ f-hidden-select f-t-body ]'
                      onChange={handleSorting}>
                      {[
                        {
                          name: 'Direkt zum Anbieter',
                          value: 'default',
                          isDefault: true
                        },
                        ...vendorSelectValues
                      ].map((el, index) => {
                        if (el.isDefault) {
                          return (
                            <option
                              disabled
                              hidden
                              value={el.value}
                              key={`vendor_direct_${index}`}>
                              {el.name}
                            </option>
                          )
                        }

                        return (
                          <option key={`vendor_direct_${index}`} value={el.url}>
                            {el.name}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                )}

                {apiResult.total_count > 0 && availableSorting.length > 0 && (
                  <div className='[ f-pos-rel ] SearchResults-sorting'>
                    <span className='[ f-t-micro f-c-fo-sh080 ]'>
                      {params.order_by &&
                      availableSorting.find(el => el.value === params.order_by)
                        ? availableSorting.find(
                            el => el.value === params.order_by
                          ).label
                        : availableSorting.find(el => el.isDefault).label}
                      <Icon
                        icon='sort-16px'
                        className='[ f-icon-020 f-c-st-ter f-m-l-010 f-icon-t-mini ]'
                      />
                    </span>
                    <select
                      name='sorting'
                      className='[ f-hidden-select f-t-body ]'
                      onChange={handleSorting}
                      defaultValue={params.order_by}>
                      {availableSorting.map((el, index) => {
                        return (
                          <option key={`sorting_${index}`} value={el.value}>
                            {el.label}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                )}
              </div>
            )}
            {results}
          </div>
          {type === 'vendors' && (
            <div
              className='[ f-hidden:m f-hidden:l ]'
              style={{
                position: 'sticky',
                bottom: 0,
                textAlign: 'center',
                padding: '16px'
              }}>
              <CTA type='block-secondary' tag='a' href={getUrl('vendorMap')()}>
                <Icon
                  icon='map-16px'
                  className='[ f-icon-020 f-c-fi-inherit f-icon-t-mini f-m-r-010 ]'
                />
                Karte anzeigen
              </CTA>
            </div>
          )}
        </div>
      ) : (
        <div className='SearchResultsPage-loader' />
      )}
    </>
  )
}

export default SearchResults
